import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { environment } from 'environments/environment';

@Injectable({
    providedIn: 'root',
})
export class AdvertisingCampaignService {
    private _campaign: any = null;
    private readonly uploadUrl = `${environment.apiNest_2}/api/v1/upload/service`;
    private readonly deleteUrl = `${environment.apiNest_2}/api/v1/upload/service/delete-files`;
    private readonly baseCampaignUrl = `${environment.apiNest_2}/api/v1/advertisings`;
    private baseUrlReservations = `${environment.apiNest_2}/api/v1/reservations`;
    private readonly paymentUrlDirect = `${environment.apiNest_2}/api/v1/payments/direct-payment`;
    private readonly paymentUrl = `${environment.apiNest_2}/api/v1/payments/create-intent`;
   private baseUrlClub = `${environment.apiNest_2}/api/v1/club`;
    private draftCampaign: any = null;

    constructor(private http: HttpClient) {}

    // ===============================
    //  Gestion temporaire de la campagne en mémoire
    // ===============================
    private fromPaymentSummary = false;

    setFromPaymentSummary(value: boolean) {
        this.fromPaymentSummary = value;
    }

    getFromPaymentSummary(): boolean {
        return this.fromPaymentSummary;
    }

    setCampaign(data: any): void {
        this._campaign = data;
    }

    getCampaign(): any {
        return this._campaign;
    }

    clearCampaign(): void {
        this._campaign = null;
    }
    //get tarif 
    getAdTarifs(clubId: string): Observable<any> {
    return this.http.get(`${this.baseUrlClub}/${clubId}/ad-tarifs`);
}

// edit tarif + paramètres financiers
updateAdTarifs(clubId: string, payload: any): Observable<any> {
  return this.http.patch(`${this.baseUrlClub}/${clubId}`, payload);
}


    // ===============================
    //  Gestion temporaire de relance  la campagne en mémoire
    // ===============================
    setDraftCampaign(data: any) {
        this.draftCampaign = data;
    }

    getDraftCampaign() {
        return this.draftCampaign;
    }

    clearDraftCampaign() {
        this.draftCampaign = null;
    }
    /**
     * Télécharge une image sur le serveur.
     * @param file Le fichier image à télécharger.
     */
    uploadImage(file: File): Observable<{ url: string }> {
        const formData = new FormData();
        formData.append('files', file);
        formData.append('source', 'photo Advertising');
        formData.append('typeMedia', 'photo');

        return this.http.post<{ url: string }>(this.uploadUrl, formData);
    }

    /**
     * Supprime une image du serveur.
     * @param url L'URL de l'image à supprimer.
     * @returns Un observable contenant la réponse du serveur.
     */
    deleteImageFromServer(url: string): Observable<any> {
        if (!url) {
            console.error('URL de suppression invalide.');
            return new Observable<any>();
        }

        const params = new HttpParams().append('urls', url);
        return this.http.post<any>(this.deleteUrl, null, {
            params,
            responseType: 'text' as 'json',
        });
    }

    /**
     * Crée une campagne publicitaire.
     * @param campaignData Les données de la campagne à créer.
     */
    createAdvertisingCampaign(campaignData: any): Observable<any> {
        return this.http.post<any>(this.baseCampaignUrl, campaignData, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
        });
    }

    /**
     * Récupère toutes les campagnes publicitaires.
     * @returns Un tableau d'objets de campagne publicitaire.
     */
    getAllAdvertisingCampaigns(): Observable<any[]> {
        return this.http.get<any[]>(this.baseCampaignUrl).pipe(
            map((data) =>
                data.sort((a, b) => {
                    const dateA = new Date(a.created_at).getTime();
                    const dateB = new Date(b.created_at).getTime();
                    return dateB - dateA;
                })
            )
        );
    }
    /**
     * Récupère une campagne publicitaire par son ID.
     * @param id L'ID de la campagne à récupérer.
     */
    getAdvertisingCampaignsUserConected(): Observable<any[]> {
        return this.http.get<any[]>(`${this.baseCampaignUrl}/me`).pipe(
            map((data) =>
                data.sort((a, b) => {
                    const dateA = new Date(a.created_at).getTime();
                    const dateB = new Date(b.created_at).getTime();
                    return dateB - dateA; // Tri du plus récent au plus ancien
                })
            )
        );
    }

    /**
     * Récupère une campagne publicitaire par son ID.
     * @param id L'ID de la campagne à récupérer.
     */
    getAdvertisingById(id: string): Observable<any> {
        const url = `${this.baseCampaignUrl}/${id}`;
        return this.http.get<any>(url);
    }

    /**
     * Met à jour une campagne publicitaire.
     * @param id L'ID de la campagne à mettre à jour
     */
    updateAdvertisingCampaign(id: string, campaignData: any): Observable<any> {
        const url = `${this.baseCampaignUrl}/${id}`;
        return this.http.patch<any>(url, campaignData, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
        });
    }

    /**
     * Supprime une campagne publicitaire.
     *
     */
    deleteAdvertisingCampaign(id: string): Observable<any> {
        const url = `${this.baseCampaignUrl}/${id}`;
        return this.http.delete<any>(url);
    }

    /**
     * Récupère les publicités du jour
     * @returns Un Observable contenant un tableau de campagnes publicitaires
     */
    getTodayAds(): Observable<any[]> {
        return this.http.get<any[]>(`${this.baseUrlReservations}/today-ads`);
    }

    /**
     * Enregistre une interaction (VIEW ou CLICK) pour une pub
     * @param advertisingId L'ID de la publicité concernée
     * @param type Le type d'interaction : 'VIEW' ou 'CLICK'
     */
    trackInteraction(
        advertisingId: string,
        type: 'VIEW' | 'CLICK'
    ): Observable<any> {
        const url = `${environment.apiNest_2}/api/v1/advertising-interaction/track`;
        return this.http.post(url, {
            advertisingId,
            interaction_type: type,
        });
    }

    /**
     * Soumet une candidature avec des fichiers attachés.
     * @param formData Les données du formulaire contenant les fichiers.
     */
    submitJobApplication(
        formData: FormData,
        advertisingId: string
    ): Observable<any> {
        const url = `${environment.apiNest_2}/api/v1/advertising-interaction/apply?advertisingId=${advertisingId}`;
        return this.http.post(url, formData);
    }

    /**
     * Vérifie si l'utilisateur connecté a déjà postulé à une offre
     * @param advertisingId L'ID de la publicité
     */
    isSubmitJobApplication(
        advertisingId: string
    ): Observable<{ hasApplied: boolean }> {
        const url = `${environment.apiNest_2}/api/v1/advertising-interaction/has-applied/${advertisingId}`;
        return this.http.get<{ hasApplied: boolean }>(url);
    }

    /**
     * Récupère les statistiques d'une publicité
     * @param advertisingId L'ID de la publicité
     */
    getAdvertisingStats(advertisingId: string): Observable<any> {
        const url = `${environment.apiNest_2}/api/v1/advertising-interaction/stats/${advertisingId}`;
        return this.http.get<any>(url);
    }

    /**
     * Lance un paiement direct (ex: Stripe)
     */
    startDirectPayment(payload: {
        amount: number;
        currency: string;
        success_url: string;
        cancel_url: string;
        transaction_type: string;
    }): Observable<{ url: string }> {
        return this.http.post<{ url: string }>(this.paymentUrlDirect, payload);
    }
    /**
     *
     */
    startPayment(payload: {
        amount: number;
        currency: string;
        success_url: string;
        cancel_url: string;
        transaction_type: string;
    }): Observable<{ url: string }> {
        return this.http.post<{ url: string }>(this.paymentUrl, payload);
    }

    enableClubAdvertisingDisplay(
        clubId: string,
        is_show: boolean
    ): Observable<any> {
        const url = `${environment.apiNest_2}/api/v1/club/${clubId}/show-advertising`;
        return this.http.patch(url, { is_show_advertising: is_show });
    }
    /**
     * Récupère la valeure de is_show_advertising
     */
    getPublicAdvertisingVisibility(
        clubId: string
    ): Observable<{ is_show_advertising: boolean }> {
        const url = `${environment.apiNest_2}/api/v1/club/${clubId}/public-advertising-visibility`;
        return this.http.get<{ is_show_advertising: boolean }>(url);
    }

    sendPaymentLinkToThirdParty(payload: {
        advertising_id: string;
        recipient_email: string;
        success_url: string;
        cancel_url: string;
        stripe_url?: string;
    }): Observable<any> {
        return this.http.post(
            `${environment.apiNest_2}/api/v1/payments/send-link-to-third`,
            payload
        );
    }

    /**
     * Confirme un paiement à partir du transactionId
     */
    confirmPayment(transactionId: string): Observable<any> {
        const url = `${environment.apiNest_2}/api/v1/payments/confirm-payment`;
        return this.http.post(url, { transactionId });
    }
    /**
     * Annule un paiement à partir du transactionId
     */
    cancelPayment(transactionId: string): Observable<any> {
        const url = `${environment.apiNest_2}/api/v1/payments/cancel-payment`;
        return this.http.post(url, { transactionId });
    }
    /**
     * getPaymentStatus
     */
    getPaymentStatus(transactionId: string) {
        return this.http.get<{ status: string }>(
            `${environment.apiNest_2}/api/v1/payments/status/${transactionId}`
        );
    }

    getCurrencySymbol(code: string): string {
    const map: Record<string, string> = {
        EUR: '€',
        USD: '$',
        GBP: '£',
        CHF: 'CHF',
        CAD: 'CAD',
        JPY: '¥',
    };
    return map[code] ?? code;
}
}
